import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether an element has scrolled into view YET — one-way, and never back.
 *
 * A plain `IntersectionObserver` rather than a library hook, and it stays JavaScript rather than
 * becoming a scroll-driven CSS animation, because both callers need something a `view()` timeline
 * cannot express: the reveal is an ENTRANCE that happens once, and its animations are staggered with
 * `animation-delay`, which a timeline-driven animation ignores. Tying them to a timeline would rewind
 * every box on the way back up and collapse the sweep into a single beat.
 *
 * `false` on the server and on the first client render alike, so hydration sees one answer and the
 * element starts un-arrived wherever it is first painted. Once true the observer is torn down: there
 * is nothing left to watch.
 */
export function useInView(ref: RefObject<Element | null>, { margin }: { margin?: string } = {}) {
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element || inView) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) setInView(true);
			},
			// Named `margin` to match what the call sites read as: the box the observer tests against,
			// grown or shrunk. Left off, it is the viewport exactly.
			margin ? { rootMargin: margin } : undefined,
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, [ref, margin, inView]);

	return inView;
}
